import * as THREE from 'three';
import type { River } from '../game/River';
import type { Tuning } from '../game/tuning';
import { disposeObject3D } from '../utils/dispose';
import type { Duck } from './Duck';

export type Item = 'boost' | 'shield' | 'lure';
export const ITEMS: readonly Item[] = ['boost', 'shield', 'lure'];
export const ITEM_ICON: Record<Item, string> = { boost: '⚡', shield: '🫧', lure: '🍞' };
export const ITEM_NAME: Record<Item, string> = { boost: 'Squeaky boost', shield: 'Bubble shield', lure: 'Bread lure' };
/** Each item's bubble colour, so you can tell boxes apart from a distance. */
const ITEM_COLOR: Record<Item, string> = { boost: '#ffb000', shield: '#35c8ff', lure: '#ff7a45' };

/** Rows of item boxes: [distance, metres from the fast lane]. Two per row, either side. */
const BOX_ROWS: readonly [d: number, offset: number][] = [
  [16, 0.9],
  [51, 1.2], // beside the whirlpool
  [72, 0.9],
  [96, 0.9],
  [116, 0.9], // in the shortcut chute
  [150, 0.9],
  [186, 1.1],
  [214, 0.9], // rapids
  [256, 0.8], // inside the pipe
  [276, 0.9], // between the herons
];
const BOX_RADIUS = 0.58;
const PICKUP_RADIUS = 1;
const BOX_RESPAWN = 4;

const BOOST_KICK = 5.5;
const BOOST_GLIDE = 1.4;
const SHIELD_SECONDS = 12;
const LURE_SECONDS = 3.5;
const LURE_RANGE = 9;
/** Sideways pull (m/s²) a lure puts on nearby ducks. */
const LURE_PULL = 5;

type Box = {
  item: Item;
  x: number;
  y: number;
  z: number;
  respawn: number;
  mesh: THREE.Group;
  ring: THREE.Mesh;
};
type Lure = { x: number; z: number; age: number; owner: number; mesh: THREE.Group };

export type PowerUpEvents = {
  /** Item the player just picked up. */
  picked: Item | null;
  /** Where that box was (world space), for the HUD's fly-in. */
  pickedAt: { x: number; y: number; z: number };
  /** The player is being pulled by someone else's lure this frame. */
  lured: boolean;
  /** A rival just used this item (for callouts). */
  rivalUsed: Item | null;
};

/**
 * Item boxes on the river, what each duck is holding, and live bread lures.
 * Index 0 is always the player.
 */
export class PowerUps {
  readonly group = new THREE.Group();
  readonly events: PowerUpEvents = { picked: null, pickedAt: { x: 0, y: 0, z: 0 }, lured: false, rivalUsed: null };
  readonly held: (Item | null)[];
  private readonly boxes: Box[] = [];
  private readonly lures: Lure[] = [];
  private readonly flow = { x: 0, z: 0, speed: 0, whirl: 0 };

  constructor(
    private readonly river: River,
    rng: () => number,
    duckCount: number,
  ) {
    this.held = new Array<Item | null>(duckCount).fill(null);
    const bubbleGeometry = new THREE.SphereGeometry(BOX_RADIUS, 24, 18);
    const ringGeometry = new THREE.RingGeometry(0.72, 0.9, 32);
    const makeLook = (item: Item) => {
      const color = ITEM_COLOR[item];
      return {
        shell: new THREE.MeshStandardMaterial({
          color,
          transparent: true,
          opacity: 0.32,
          roughness: 0.05,
          emissive: color,
          emissiveIntensity: 0.4,
          depthWrite: false,
        }),
        ring: new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.75, depthWrite: false }),
      };
    };
    const look = { boost: makeLook('boost'), shield: makeLook('shield'), lure: makeLook('lure') };

    for (const [d, offset] of BOX_ROWS) {
      // Each pair offers two different items: a choice, not a lottery.
      const first = ITEMS[Math.floor(rng() * ITEMS.length)];
      const rest = ITEMS.filter((item) => item !== first);
      const second = rest[Math.floor(rng() * rest.length)];
      for (const [k, side] of [-1, 1].entries()) {
        const item = k === 0 ? first : second;
        const x = river.laneX(d) + side * offset;
        const y = river.heightAt(d, x);
        const mesh = new THREE.Group();
        const bubble = new THREE.Mesh(bubbleGeometry, look[item].shell);
        // A little toy of the item spins inside the bubble: you see what you'll get.
        const toy = buildItemToy(item);
        toy.name = 'toy';
        toy.scale.setScalar(1.3);
        bubble.renderOrder = 2;
        mesh.add(toy, bubble);
        const ring = new THREE.Mesh(ringGeometry, look[item].ring);
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(x, y + 0.04, -d);
        ring.renderOrder = 2;
        mesh.position.set(x, y + 0.65, -d);
        this.group.add(mesh, ring);
        this.boxes.push({ item, x, y: y + 0.65, z: -d, respawn: 0, mesh, ring });
      }
    }
  }

  reset(): void {
    this.held.fill(null);
    for (const box of this.boxes) box.respawn = 0;
    for (const lure of this.lures) this.group.remove(lure.mesh);
    this.lures.length = 0;
  }

  update(delta: number, time: number, ducks: Duck[]): void {
    this.events.picked = null;
    this.events.lured = false;
    this.events.rivalUsed = null;

    for (const [k, box] of this.boxes.entries()) {
      box.respawn = Math.max(0, box.respawn - delta);
      const visible = box.respawn <= 0;
      box.mesh.visible = visible;
      box.ring.visible = visible;
      box.mesh.position.set(box.x, box.y + Math.sin(time * 2.2 + k) * 0.08, box.z);
      const toy = box.mesh.getObjectByName('toy');
      if (toy) toy.rotation.y = time * 1.6 + k;
      box.ring.scale.setScalar(1 + Math.sin(time * 4 + k) * 0.08);
      if (!visible) continue;
      ducks.forEach((duck, i) => {
        if (box.respawn > 0 || this.held[i] !== null) return;
        if (Math.hypot(duck.position.x - box.x, duck.position.z - box.z) > PICKUP_RADIUS) return;
        if (Math.abs(duck.position.y - box.y) > 1.5) return;
        const item = box.item;
        this.held[i] = item;
        box.respawn = BOX_RESPAWN;
        if (i === 0) {
          this.events.picked = item;
          this.events.pickedAt = { x: box.x, y: box.y, z: box.z };
        }
      });
    }

    for (let l = this.lures.length - 1; l >= 0; l -= 1) {
      const lure = this.lures[l];
      lure.age += delta;
      // The bread floats off with the current.
      this.river.flowAt(lure.x, lure.z, this.flow);
      lure.x += this.flow.x * delta * 0.8;
      lure.z += this.flow.z * delta * 0.8;
      const y = this.river.heightAt(-lure.z, lure.x) + 0.05;
      lure.mesh.position.set(lure.x, y, lure.z);
      const pulse = 1 + Math.sin(lure.age * 8) * 0.08;
      lure.mesh.scale.setScalar(pulse * Math.min(1, lure.age * 5));
      if (lure.age > LURE_SECONDS) {
        this.group.remove(lure.mesh);
        disposeObject3D(lure.mesh);
        this.lures.splice(l, 1);
        continue;
      }
      ducks.forEach((duck, i) => {
        if (i === lure.owner || duck.inAir) return;
        const dx = lure.x - duck.position.x;
        const dz = lure.z - duck.position.z;
        const dist = Math.hypot(dx, dz);
        if (dist > LURE_RANGE || dist < 0.3) return;
        const strength = LURE_PULL * (1 - dist / LURE_RANGE) + LURE_PULL * 0.35;
        duck.velocity.x += (dx / dist) * strength * delta;
        duck.velocity.z += (dz / dist) * strength * delta * 0.35;
        if (i === 0) this.events.lured = true;
      });
    }
  }

  /** Nearest lure a rival should chase, if one is pulling it. */
  lureFor(index: number, duck: Duck): { x: number; z: number } | null {
    for (const lure of this.lures) {
      if (lure.owner === index) continue;
      if (Math.hypot(lure.x - duck.position.x, lure.z - duck.position.z) < LURE_RANGE) return lure;
    }
    return null;
  }

  /** Use the item duck `index` is holding. Returns what was used. */
  use(index: number, ducks: Duck[], river: River, tuning: Tuning): Item | null {
    const item = this.held[index];
    if (!item) return null;
    const duck = ducks[index];
    this.held[index] = null;
    if (item === 'boost') {
      duck.boost(BOOST_KICK, BOOST_GLIDE, river, tuning);
    } else if (item === 'shield') {
      duck.shieldTime = SHIELD_SECONDS;
    } else {
      this.throwLure(index, ducks);
    }
    if (index !== 0) this.events.rivalUsed = item;
    return item;
  }

  /** Drop the bread beside the nearest other duck, on the side away from its line. */
  private throwLure(owner: number, ducks: Duck[]): void {
    const me = ducks[owner];
    let target: Duck | null = null;
    let best = Infinity;
    for (let i = 0; i < ducks.length; i += 1) {
      if (i === owner) continue;
      const dist = Math.hypot(ducks[i].position.x - me.position.x, ducks[i].position.z - me.position.z);
      if (dist < best && dist < 18) {
        best = dist;
        target = ducks[i];
      }
    }
    const victim = target ?? me;
    const d = victim.distance + (target ? 3.5 : -2);
    const lane = this.river.laneX(d);
    const hw = this.river.halfWidth(d);
    const cx = this.river.centerX(d);
    // Pull them toward the slower side of the river.
    const side = lane >= cx ? -1 : 1;
    const x = Math.max(cx - hw + 0.8, Math.min(cx + hw - 0.8, lane + side * 2.6));
    const mesh = this.makeLureMesh();
    this.group.add(mesh);
    this.lures.push({ x, z: -d, age: 0, owner, mesh });
  }

  private makeLureMesh(): THREE.Group {
    const group = new THREE.Group();
    const slice = new THREE.Mesh(
      new THREE.BoxGeometry(0.75, 0.14, 0.7),
      new THREE.MeshStandardMaterial({ color: '#f1d7a0', roughness: 0.9 }),
    );
    slice.position.y = 0.05;
    const crust = new THREE.Mesh(
      new THREE.TorusGeometry(0.4, 0.07, 6, 16),
      new THREE.MeshStandardMaterial({ color: '#a8612a', roughness: 0.9 }),
    );
    crust.rotation.x = Math.PI / 2;
    crust.position.y = 0.05;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.4, 1.55, 32),
      new THREE.MeshBasicMaterial({ color: '#ffe08a', transparent: true, opacity: 0.6, depthWrite: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.03;
    group.add(slice, crust, ring);
    slice.castShadow = true;
    return group;
  }

  dispose(): void {
    disposeObject3D(this.group);
  }
}

/** Flat-shaded toy material, to match the river's low-poly look. */
function toyMaterial(color: string, glow = 0): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.45, flatShading: true, emissive: color, emissiveIntensity: glow });
}

/** A small low-poly model of the item, sized to sit inside its bubble. */
function buildItemToy(item: Item): THREE.Group {
  const toy = new THREE.Group();
  if (item === 'boost') {
    // A chunky lightning bolt.
    const bolt = new THREE.Shape();
    bolt.moveTo(0.08, 0.36);
    bolt.lineTo(-0.2, -0.02);
    bolt.lineTo(-0.02, -0.02);
    bolt.lineTo(-0.1, -0.36);
    bolt.lineTo(0.2, 0.06);
    bolt.lineTo(0.02, 0.06);
    bolt.closePath();
    const geometry = new THREE.ExtrudeGeometry(bolt, { depth: 0.12, bevelEnabled: true, bevelSize: 0.025, bevelThickness: 0.025, bevelSegments: 1 });
    geometry.center();
    toy.add(new THREE.Mesh(geometry, toyMaterial('#ffc21a', 0.45)));
  } else if (item === 'shield') {
    // A rubber swim ring, cyan with white bands.
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.09, 8, 16), toyMaterial('#35c8ff', 0.2));
    toy.add(ring);
    const band = toyMaterial('#ffffff', 0.15);
    for (let i = 0; i < 4; i += 1) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const stripe = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.095, 8, 3, 0.35), band);
      stripe.rotation.z = a;
      toy.add(stripe);
    }
  } else {
    // A toasted slice of bread, like the lure it drops.
    const slice = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.4, 0.1), toyMaterial('#f1d7a0', 0.1));
    const top = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.23, 0.1, 10, 1, false, 0, Math.PI), toyMaterial('#f1d7a0', 0.1));
    top.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    top.position.y = 0.2;
    const crust = toyMaterial('#a8612a');
    const crustTop = new THREE.Mesh(new THREE.TorusGeometry(0.23, 0.035, 5, 10, Math.PI), crust);
    crustTop.position.y = 0.2;
    const left = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.42, 0.12), crust);
    left.position.x = -0.23;
    const right = left.clone();
    right.position.x = 0.23;
    const bottom = new THREE.Mesh(new THREE.BoxGeometry(0.51, 0.05, 0.12), crust);
    bottom.position.y = -0.21;
    toy.add(slice, top, crustTop, left, right, bottom);
    toy.position.y = -0.04;
  }
  toy.traverse((object) => {
    if ((object as THREE.Mesh).isMesh) object.castShadow = true;
  });
  return toy;
}
